/**
 * RecordDetailLayout — Lightning record detail primitive.
 *
 * Round 6 Item 16 (2026-06-25). Cufflinks 2.0 — the SINGLE canonical layout
 * that every record detail page wraps. Same shape across User / PermissionSet
 * / Connector instance / Model instance / Prompt instance / Chat session /
 * Invoice / Purchase: hero (icon + breadcrumbs + title + actions) + optional
 * tabs + main content + side rail + footer (Last modified by / Created by).
 *
 * Per-role rendering = SAME primitive, DIFFERENT props. Standard-user record
 * detail and admin record detail use the same component — the admin gets the
 * override-fields tab and stamping footer; the standard user gets the basic
 * shape. Don't fork to AdminRecordDetailLayout vs UserRecordDetailLayout;
 * thread the difference through props.
 *
 * Composition rules (don't fight these):
 *   - hero is ALWAYS present (Lightning DNA — there is no record detail
 *     without a hero). Renders OUTSIDE the .record-shell grid so the
 *     highlight panel spans full width — matches existing convention.
 *   - tabs is OPTIONAL (simple records render main directly). When present,
 *     tabs nest at the top of the main column inside the grid.
 *   - rail is OPTIONAL (record context surfaces — assignments, audit summary,
 *     related items). Uses the existing .record-rail CSS class.
 *   - footer is OPTIONAL but recommended for admin-side records (stamping
 *     gives admins audit context inline). Renders below the grid.
 *
 * State machine pattern documented in PATTERNS.md:
 *   - loading → renders <Skeleton>-shaped hero + rail + main
 *   - empty   → renders <EmptyState> in main area (no record found)
 *   - loaded  → renders the populated hero + tabs + main + rail + footer
 *   - error   → renders inline <SectionErrorBoundary> fallback per section
 *
 * The state machine is owned by the caller (consumer of this primitive
 * passes the resolved record via `record={...}` or null). This primitive
 * doesn't fetch.
 */

import type { ReactNode } from 'react';

import { SkeletonBlock } from './Skeleton.tsx';

export interface RecordDetailLayoutProps {
  /**
   * The hero block at the top of the page. Renders the icon + breadcrumbs +
   * title + primary/secondary action cluster. Required — every record detail
   * page has a hero per the Lightning DNA. Renders ABOVE the .record-shell
   * grid so the highlight panel spans full width.
   */
  hero: ReactNode;
  /**
   * Optional tab strip below the hero. Pass the same Tabs primitive used by
   * other record detail pages (LightningTabs.tsx). When omitted, content
   * renders directly without tabs.
   */
  tabs?: ReactNode;
  /**
   * Main content area. Wrapped in the canonical record-shell main column.
   */
  main: ReactNode;
  /**
   * Optional side rail (right column). Render assignments + audit summary +
   * related items via lightning-section cards. When omitted, main takes full
   * width.
   */
  rail?: ReactNode;
  /**
   * Optional footer block — "Last modified by {name} at {time} • Created by
   * {name} at {time}". When provided, renders muted small text below main.
   * Per-role: admin records typically pass this; standard-user records
   * typically don't (less audit-relevant for the user's own data).
   */
  footer?: ReactNode;
  /**
   * Loading state. When true, renders Skeleton-shaped placeholders for the
   * hero + main + rail. Consumer should NOT also pass hero / main / rail when
   * loading — primitive handles the swap.
   */
  loading?: boolean;
  /**
   * Empty state. When true, renders the empty fallback in the main area
   * (caller passes the empty content via `emptyState`). Hero + tabs still
   * render normally; this only swaps the main + rail.
   */
  isEmpty?: boolean;
  /**
   * Content to render when isEmpty=true. Typically <EmptyState />.
   */
  emptyState?: ReactNode;
}

/**
 * The canonical record detail page wrapper. Compose a record detail page
 * by passing the resolved hero / tabs / main / rail / footer plus the
 * loading + empty flags.
 *
 * Example usage:
 *
 *   function ConnectorDetail() {
 *     const { data, loading } = useConnectorDetail(slug);
 *     return (
 *       <RecordDetailLayout
 *         loading={loading}
 *         isEmpty={!loading && data === null}
 *         emptyState={<EmptyState title="Connector not found." />}
 *         hero={<ConnectorHero connector={data} />}
 *         tabs={<LightningTabs active={tab} ... />}
 *         main={<ConnectorOverviewTab connector={data} />}
 *         rail={<ConnectorRail connector={data} />}
 *         footer={<RecordFooter created={...} updated={...} />}
 *       />
 *     );
 *   }
 */
export function RecordDetailLayout({
  hero,
  tabs,
  main,
  rail,
  footer,
  loading = false,
  isEmpty = false,
  emptyState,
}: RecordDetailLayoutProps) {
  if (loading) {
    // Loading shape mirrors the canonical record-page shape: hero placeholder
    // outside the .record-shell grid, then the 2-col grid (main + optional
    // rail) inside. .record-shell + .record-rail are the existing CSS
    // contract (apps/ui/src/styles/app.css line 4107+). Keep the primitive
    // riding the existing classes rather than introducing parallel CSS.
    // Push 4-1v (2026-07-09): loading skeleton mirrors the same no-rail
    // width fix — single-column shell when caller isn't going to render a
    // rail, so the skeleton width matches the eventual rendered width.
    const loadingShellClass = rail !== undefined
      ? 'record-shell'
      : 'record-shell record-shell--no-rail';
    return (
      <div>
        <SkeletonBlock height={120} />
        <div className={loadingShellClass} style={{ marginTop: 16 }}>
          <div style={{ minWidth: 0 }}>
            <SkeletonBlock height={200} />
            <SkeletonBlock height={120} />
          </div>
          {rail !== undefined && (
            <aside className="record-rail">
              <SkeletonBlock height={140} />
            </aside>
          )}
        </div>
      </div>
    );
  }

  // Canonical record-page shape (matches the existing pre-primitive convention
  // used by AdminConnectorDetail / ConnectorDetail / KnowledgeDetail / etc.):
  //   <div>
  //     {hero}                                  ← .lightning-highlight, OUTSIDE the grid
  //     <div className="record-shell">          ← 2-col grid (main + rail)
  //       <div>{tabs}{main}</div>               ← col 1 (main); tabs nest above main content
  //       <aside className="record-rail">{rail}</aside>  ← col 2
  //     </div>
  //     {footer}                                ← below the grid
  //   </div>
  //
  // The .record-shell CSS (app.css ~4107) does the grid; .record-rail (4118)
  // styles the rail column. The primitive doesn't introduce new class names;
  // it just enforces the consistent slot composition.
  // Push 4-1v (2026-07-09): when no rail is passed (or the page is in
  // empty state), collapse .record-shell to a single full-width column so
  // the main content matches the hero panel's width. Prevents phantom-rail
  // whitespace on rail-less detail pages like ActivityDetail per Ean nitpick
  // "no awkward spacing on record detail is a good thing to shoot for."
  const shellHasRail = rail !== undefined && !isEmpty;
  const shellClass = shellHasRail ? 'record-shell' : 'record-shell record-shell--no-rail';
  return (
    <div>
      {hero}
      <div className={shellClass}>
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--card-gap)' }}>
          {tabs}
          {isEmpty && emptyState !== undefined ? emptyState : main}
        </div>
        {shellHasRail && (
          <aside className="record-rail">{rail}</aside>
        )}
      </div>
      {footer !== undefined && !isEmpty && footer}
    </div>
  );
}

/**
 * RecordFooter — canonical "Last modified by {name} at {time} • Created by
 * {name} at {time}" footer used inside RecordDetailLayout's footer slot.
 *
 * Reuse this exact shape across every record detail. Renders muted small
 * text right-aligned, breaks to its own line on narrow viewports.
 */
export function RecordFooter({
  createdAt,
  createdBy,
  updatedAt,
  updatedBy,
  formatTime,
}: {
  createdAt: string | null;
  createdBy: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  /** Override the default Intl format if the caller has a timezone-aware
   *  formatter handy (see Item 23 schema proposal — when timezone column
   *  ships, every callsite passes its formatTimestamp helper). */
  formatTime?: (iso: string) => string;
}) {
  const fmt = formatTime ?? defaultFormatTime;
  const pieces: string[] = [];
  if (updatedAt !== null && updatedBy !== null) {
    pieces.push(`Last modified by ${updatedBy} at ${fmt(updatedAt)}`);
  }
  if (createdAt !== null && createdBy !== null) {
    pieces.push(`Created by ${createdBy} at ${fmt(createdAt)}`);
  }
  if (pieces.length === 0) return null;
  return (
    <div
      style={{
        marginTop: 24,
        fontSize: 11,
        color: 'var(--fg-muted)',
        textAlign: 'right',
        lineHeight: 1.6,
      }}
    >
      {pieces.join(' • ')}
    </div>
  );
}

function defaultFormatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// Re-export SkeletonBlock for callers building loading hero variants without
// a separate import line. Keeps the consumer file's imports tight.
export { SkeletonBlock };
